import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UrlFormatter } from 'shared';
import { IdentityService } from 'account-hybrid/core/authentication';
import { ParseService } from './parse.service';

/**
 * Native-Angular port of the AngularJS `User` service
 * (account-v1/src/core/api/user.js). Verbatim per the inc-2a…2e recipe: Promise
 * methods via `firstValueFrom` (= the v1 `$http…then(r => r.data)`), relative
 * `/api/...` URLs (literal where v1 was literal, the byte-identical
 * `UrlFormatter.format` where v1 formatted), `parse.arrayOrDefault` via the native
 * `ParseService`, and `unImpersonate`'s `identity.logout()` via the native
 * `IdentityService` (the v1 DI name was `downgradeInjectable(IdentityService)` —
 * same instance). `login`/`logout` resolve like v1 (logout never unwrapped `.data`;
 * its only consumer ignores the value).
 *
 * Named `UserV1ApiService` (port of the v1 `User` factory) — the app has no other
 * User API client, but the V1 suffix keeps the family consistent with
 * `GuestsV1ApiService`.
 *
 * WS5 #10 Stage A inc 2g: replaces the `USER_API_TOKEN` bridge (app.effects
 * signOut$ was its only consumer). `providedIn:'root'` so consumers `inject()` it.
 */
@Injectable({ providedIn: 'root' })
export class UserV1ApiService {
  private readonly http = inject(HttpClient);
  private readonly parse = inject(ParseService);
  private readonly identity = inject(IdentityService);

  /** Login. */
  login(model: any): Promise<any> {
    return firstValueFrom(this.http.post<any>('/api/user/login', model));
  }

  /** Logout. */
  logout(): Promise<any> {
    return firstValueFrom(this.http.post<any>('/api/auth/logout', null));
  }

  /** Un-impersonate. */
  unImpersonate(): Promise<void> {
    return firstValueFrom(this.http.post<any>('/api/user/unimpersonate', null)).then(() => {
      // PRESERVE-BUGS: v1 calls `identity.logout()`, but the v1 IdentityService
      // factory (which had logout) was obsoleted by the downgraded NATIVE
      // IdentityService — which has no `logout`. So v1 TODAY throws a TypeError
      // here after the POST succeeds (the returned promise rejects). Reproduced
      // verbatim; the cast is what v1's untyped DI did implicitly.
      (this.identity as any).logout();
    });
  }

  /** Info. */
  info(): Promise<any> {
    return firstValueFrom(this.http.get<any>('/api/user/info'));
  }

  /** Get list of properties. */
  getProperties(): Promise<any> {
    return firstValueFrom(this.http.get<any>('/api/user/properties')).then((data) => this.parse.arrayOrDefault(data));
  }

  /** Get list of collections. */
  getCollections(): Promise<any> {
    return firstValueFrom(this.http.get<any>('/api/user/collections')).then((data) => this.parse.arrayOrDefault(data));
  }

  /** Create a collection. */
  createCollection(model: any): Promise<any> {
    return firstValueFrom(this.http.post<any>('/api/user/collections', model));
  }

  /** Update a collection. */
  updateCollection(model: any): Promise<any> {
    return firstValueFrom(this.http.put<any>('/api/user/collections', model));
  }

  /** Delete a collection. */
  deleteCollection(model: any): Promise<any> {
    return firstValueFrom(this.http.delete<any>(UrlFormatter.format('/api/user/collections/:collectionUUID', model, true)));
  }

  /** Reset password. */
  resetPassword(email: string): Promise<any> {
    return firstValueFrom(this.http.post<any>('/api/user/reset-password', { email }));
  }

  /** Change password. */
  changePassword(model: any): Promise<any> {
    return firstValueFrom(this.http.post<any>('/api/user/change-password', model));
  }

  /** Search keyword. */
  searchKeyword(propertyUUID: string, search: string): Promise<any> {
    return firstValueFrom(this.http.get<any>(UrlFormatter.format('/api/user/search', { propertyUUID, search }, true)));
  }
}
